import { Map, fromJS, List, Set } from 'immutable'
import { createSelector } from 'reselect'
import { createToJsSelector } from 'selectors/utils'
import {
  getCanEditViewChecPlusVehicle,
  getCanEditViewM2Vehicle,
} from 'selectors/user'
import {
  VEHICLE_TYPE_CHEC_PLUS,
  VEHICLE_TYPE_M2,
  TRUCK,
  MODULE_MAX_PINS,
  ECP,
  ECU,
  MAX_SWITCH_INDICATORS,
  MAX_CAN_INPUTS,
  BUS_MODULES,
  MIN_APP_VARIANT_FOR_SWITCH_INDICATOR,
} from 'utils/constants'
import {
  get,
  map,
  safeToJS,
  getEq,
  pick,
  getOr,
  getInOr,
  pickToJS,
  getIn,
} from 'utils/immutable'
import { compareRpBaseNumber } from 'utils/referenceParameter'
import { getParValidationState } from 'utils/parValidation'
import { identity, isNil, equals, cond, always, T, and } from 'ramda'

const safeGet = (value) => (obj) => {
  if (!obj) return null
  return get(value, obj)
}

const stateSelector = (state) => state

const truckSelector = (state, props) =>
  state.getIn(['truck', 'vehicles', props.serialNumber])

const moduleListSelector = (state, props) =>
  getInOr(List(), ['truck', 'vehicles', props.serialNumber, 'modules'], state)

const logicAndTimerTotalsAndLimitsByModuleSelector = createSelector(
  moduleListSelector,
  (modules) => {
    const totalsByModule = modules.reduce((acc, curr) => {
      const module = curr.get('id')
      const data = pick(
        ['logicCount', 'maxLogic', 'timerCount', 'maxTimer'],
        curr
      )

      return acc.set(module, data)
    }, Map())

    return totalsByModule
  }
)

export const getLogicAndTimerTotalsAndLimitsByModule = createSelector(
  logicAndTimerTotalsAndLimitsByModuleSelector,
  safeToJS
)

const featuresSelector = (state, props) =>
  state.getIn(['truck', 'vehicles', props.serialNumber, 'features'])

const commentsSelector = (state, props) =>
  state.getIn(['truck', 'vehicles', props.serialNumber, 'comments'])

const tempCommentSelector = (state, props) =>
  state.getIn(['truck', 'vehicles', props.serialNumber, 'tempComment'])

const vinSelector = (state, props) =>
  state.getIn(['truck', 'vehicles', props.serialNumber, 'vehicleVin'])

const primaryModuleVersionSelector = (state, props) =>
  getInOr(
    '',
    ['truck', 'vehicles', props.serialNumber, 'primaryModuleVersion'],
    state
  )

const vehicleLowContentBhmSelector = (state, props) =>
  getInOr(
    false,
    ['truck', 'vehicles', props.serialNumber, 'isLowContentBhm'],
    state
  )

const vehicleLowContentChmSelector = (state, props) =>
  getInOr(
    false,
    ['truck', 'vehicles', props.serialNumber, 'isLowContentChm'],
    state
  )

export const vehicleLowContentBhmChmDetails = createSelector(
  vehicleLowContentBhmSelector,
  vehicleLowContentChmSelector,
  (isLowContentBhm, isLowContentChm) => ({ isLowContentBhm, isLowContentChm })
)

export const getPrimaryModuleVersion = createSelector(
  primaryModuleVersionSelector,
  safeToJS
)

const engineFamilySelector = (state, props) =>
  state.getIn(['truck', 'vehicles', props.serialNumber, 'engine'])

const transmissionFamilySelector = (state, props) =>
  state.getIn(['truck', 'vehicles', props.serialNumber, 'transmission'])

export const parFileValidationDataSelector = createSelector(
  truckSelector,
  getOr(null, 'parFileValidation')
)

export const vehicleReferenceParametersSelector = createSelector(
  truckSelector,
  get('referenceParameters')
)

export const vehicleResourceDataSelector = createSelector(
  truckSelector,
  pick(['maxTimerCount', 'maxLogicCount', 'timerCount', 'logicCount'])
)

/**
 * A selector for vehicle databook codes.
 *
 * SerialNumber is required for truckSelector
 * @param {{ string }} object.serialNumber
 * @return {List()}
 */
export const getVehicleDataBookCodes = createSelector(
  truckSelector,
  getOr(List(), 'dbCodes')
)

export const getVehicleIsLoading = createSelector(
  truckSelector,
  get('isLoading') // returning undefined is a good flag for not truck yet so probably still loading
)

// Tracks the truck-details fetch lifecycle (REQUESTING_TRUCK_DETAILS → GET_TRUCK_DETAILS).
// Returns undefined when the vehicle is not in the store yet, true while loading, false when done.
// Use this (not getVehicleIsLoading) anywhere that gates on truck-details availability.
export const getVehicleDetailsLoading = createSelector(
  truckSelector,
  get('vehicleDetailsLoading')
)

export const getModuleList = createToJsSelector(
  createSelector(moduleListSelector, map(get('id')))
)

const transformSlotData = (slots) =>
  slots.reduce((acc, slot) => {
    const { id, location, uri } = pickToJS(['id', 'location', 'uri'], slot)
    const solenoidId = getIn(['solenoid', 'functionId'], slot)
    const pending = getIn(['pending'], slot)
    const transformedSlotMap = new Map({
      id,
      location,
      uri,
      solenoid: solenoidId,
      originalSolenoid: solenoidId,
      pending,
    })
    return acc.set(location, transformedSlotMap)
  }, Map())

const transformModuleSolenoids = (slots) =>
  slots.reduce((slotAcc, slotData) => {
    const slot = getOr(null, 'solenoid', slotData)
    if (isNil(slot)) return slotAcc
    const slotId = slot.get('functionId')
    return slotAcc.set(slotId, slot)
  }, Map())

export const getECPModuleList = createSelector(
  moduleListSelector,
  (modules) => {
    const ecpModules = modules.filter((module) => module.get('type') === ECP)
    if (!ecpModules) return Map()
    const transformedECPModuleData = ecpModules.reduce((acc, moduleData) => {
      const slotsData = moduleData.get('slots')
      const slotLocations = slotsData.map((slot) => slot.get('location'))
      const { ecuVariant, id, isServiced, maxSolenoids, type, uri } = pickToJS(
        ['ecuVariant', 'id', 'isServiced', 'maxSolenoids', 'type', 'uri'],
        moduleData
      )

      const transformedSlotData = transformSlotData(slotsData)
      const transformedModuleMap = fromJS({
        [id]: {
          ecuVariant,
          id,
          isServiced,
          maxSolenoids,
          type,
          uri,
          slots: slotLocations,
        },
      })
      const transformedModuleSolenoids = transformModuleSolenoids(slotsData)

      const accModule = acc.get('module')
      const accSlots = acc.get('slots')
      const accModuleSolenoids = acc.get('moduleSolenoids')
      return Map({
        module: accModule.merge(transformedModuleMap),
        slots: accSlots.merge(transformedSlotData),
        moduleSolenoids: accModuleSolenoids.merge(transformedModuleSolenoids),
      })
    }, Map({ module: Map(), slots: Map(), moduleSolenoids: Map() }))
    return transformedECPModuleData
  }
)

const getTruckState = (state) => state.get('truck')

const getTruckDataBySerial = (serialNumber, state) => {
  const truckState = getAllTrucks(state)
  const { trucks } = truckState
  const selectedTruck = trucks.get(serialNumber)
  if (!selectedTruck) return null
  return selectedTruck
}

export const getTruckDataBySerialNumber = (serialNumber, state) =>
  getTruckDataBySerial(serialNumber, state).toJS()

// Temporary
export function vehicleDetailsLoading(state, serialNumber) {
  const truck = getTruckDataBySerial(serialNumber, state)
  if (!truck) return null
  return truck.get('vehicleDetailsLoading')
}

export const selectVehicleBySerial = createSelector(truckSelector, (truck) =>
  safeToJS(truck)
)

export const selectServiceRecordModal = createSelector(getTruckState, (truck) =>
  safeToJS(truck.get('serviceRecordModal'))
)

export const makeselectVehicleBySerial = () => selectVehicleBySerial

const getTruck = (state, { serialNumber }) =>
  state.getIn(['truck', 'vehicles', serialNumber])

export const getVehicleServiceRecordDate = (state, serialNumber) => {
  const truck = getTruckDataBySerial(serialNumber, state)
  if (!truck) return false
  return truck.get('serviceDate')
}

export const getTruckSerialNumber = createSelector(
  getTruck,
  safeGet('serialNumber')
)

export const getVehicleDetailsError = createSelector(
  getTruck,
  safeGet('vehicleDetailsError')
)

export const getTruckFeatures = createSelector(featuresSelector, safeToJS)

export const getVehicleComments = createSelector(commentsSelector, safeToJS)

export const getVehicleTempComment = createSelector(
  tempCommentSelector,
  safeToJS
)

export const getTruckPrimaryModule = createSelector(
  getTruck,
  get('primaryModule')
)

export const getIsLeadVin = createSelector(getTruck, safeGet('isLeadSerial'))

export const getLeadVin = createSelector(getTruck, (truck) => {
  if (!truck) return null
  return get('leadSerialNumber')(truck)
})

export const getFleetSize = createSelector(getTruck, safeGet('fleetSize'))

export const getServiceDate = createSelector(getTruck, safeGet('serviceDate'))

export const getVehicleType = createSelector(getTruck, safeGet('vehicleType'))
export const getVehicleSubType = createSelector(getTruck, safeGet('subType'))

export const getIsChecPlus = createSelector(
  getVehicleType,
  (type) => type === VEHICLE_TYPE_CHEC_PLUS
)

export const getVehicle = createToJsSelector(createSelector(getTruck, identity))

export const getVehicleDashConfig = createToJsSelector(
  createSelector(getTruck, safeGet('dashConfig'))
)

const getTrucksToCompareData = (state) => {
  const truckState = getTruckState(state)
  const truckIds = truckState.get('trucksToCompare')
  const trucks = truckIds.map((tId) => truckState.get(TRUCK).get(tId))

  return {
    trucks,
    serviceRecordModalVisible: truckState.get('serviceRecordModalVisible'),
    serviceRecordModalAction: truckState.get('serviceRecordModalAction'),
  }
}

export const getTrucksToCompare = createSelector(
  [getTrucksToCompareData],
  (truckState) => ({
    ...truckState,
    serviceRecordModalVisible: truckState.serviceRecordModalVisible,
    serviceRecordModalAction: truckState.serviceRecordModalAction,
  })
)

const getAllTrucksData = (state) => {
  const truckState = getTruckState(state)
  const trucks = truckState.get(TRUCK)

  return {
    trucks,
    serviceRecordModalVisible: truckState.get('serviceRecordModalVisible'),
    serviceRecordModalAction: truckState.get('serviceRecordModalAction'),
  }
}

export const getAllTrucks = createSelector(
  [getAllTrucksData],
  (truckState) => ({
    ...truckState,
  })
)

export const getVehicleAndRPEdits = (serialNumber, state) => {
  const vehicle = getTruckDataBySerial(serialNumber, state)

  if (!vehicle) {
    return {
      vehicle: null,
      stagedReferenceParameters: [],
      vehicleReferenceParameters: [],
      allVehicleReferenceParameters: [],
      rpsToBeAdded: [],
      rpsToBeReplaced: [],
    }
  }

  const pojoVehicle = vehicle.toJS()
  const stagedReferenceParameters = pojoVehicle.stagedReferenceParameters || []
  const vehicleReferenceParameters = pojoVehicle.referenceParameters || []

  return {
    vehicle: pojoVehicle,
    stagedReferenceParameters,
    vehicleReferenceParameters,
    allVehicleReferenceParameters: stagedReferenceParameters
      .map((rp) => ({ ...rp, staged: true }))
      .concat(vehicleReferenceParameters),

    rpsToBeAdded: stagedReferenceParameters,
    // Staged RPs must replace RPs existing on truck with the same base number
    rpsToBeReplaced: vehicleReferenceParameters.filter((vrp) =>
      stagedReferenceParameters.some((srp) => compareRpBaseNumber(srp, vrp))
    ),
  }
}

export const vehicleAndRpEditsSelector = createSelector(
  stateSelector,
  (_, props) => props,
  (state, { serialNumber }) => getVehicleAndRPEdits(serialNumber, state)
)

const selectVehicleOMapCircuits = (state, { serialNumber }) => {
  const rpArray = state.getIn([
    'truck',
    'vehicles',
    serialNumber,
    'referenceParameters',
  ])
  if (!rpArray) return {}
  return rpArray.toJS().reduce((accum, rp) => {
    const omaps = rp.oMaps || rp.omaps || rp.OMAPS || []
    if (!omaps || omaps.length === 0) return accum

    const {
      uri,
      refParameterNumber: itemNumber,
      itemDescription: description,
    } = rp

    const referenceBy = accum
    return {
      ...referenceBy,
      ...omaps.reduce((acc, omap) => {
        const reference = referenceBy[omap.physical_slot]
        const supportParameter = {
          id: omap.id,
          uri: omap.uri,
        }
        const newRef = {
          itemNumber,
          uri,
          description,
          supportParameter,
        }
        const updatedReferences = [].concat(reference || [], [newRef])
        return { ...acc, [omap.physical_slot]: updatedReferences }
      }, {}),
    }
  }, {})
}

export const getVehicleOMapCircuitDictionary = createSelector(
  selectVehicleOMapCircuits,
  identity
)

const updateCount = (prop, collection) => (count) =>
  count + collection.get(prop)

export const diagnosticVariantSelector = createSelector(
  moduleListSelector,
  (modules) => {
    if (modules.isEmpty()) return null
    const module = modules.first()
    const ecuVariant = module.get('ecuVariant')
    return ecuVariant
  }
)

// Reads ECP ecuVariant directly from truck state, available synchronously on
// GET_TRUCK_DETAILS success — before the ECP saga (watchFetchEcps) has a chance
// to populate the ECP Redux slice. Use this when you need the variant immediately
// after loading, e.g. to gate a 404 error without a false-positive timing window.
export const getECPVariantFromTruck = createSelector(
  moduleListSelector,
  (modules) => {
    const ecpModule = modules.find((m) => m.get('type') === ECP)
    if (!ecpModule) return null
    return ecpModule.get('ecuVariant') || null
  }
)

const vehicleModuleLogicCountSelctor = createSelector(
  featuresSelector,
  moduleListSelector,
  (_, props) => props.moduleName,
  (features, modules, moduleName) => {
    const module = modules.find(getEq('id', moduleName))
    const maxLogics = module.get('maxLogic')
    const maxTimers = module.get('maxTimer')
    const maxPins = MODULE_MAX_PINS[moduleName]
    const maxSwitchIndicators =
      module.get('switchIndicatorMax') || MAX_SWITCH_INDICATORS
    const maxCanInputs = module.get('maxCanInputs') || MAX_CAN_INPUTS
    const variant = module.get('ecuVariant')

    const initialValues = Map({
      logicCount: 0,
      timerCount: 0,
      pinCount: 0,
      canInputsCount: 0,
      maxCanInputs,
      maxLogics,
      maxTimers,
      maxPins,
      variant,
      maxSwitchIndicators,
      switchIndicatorCount: 0,
    })

    if (!features || features.isEmpty()) {
      return initialValues
    }

    const moduleFeatures = features.filter(getEq('moduleName', moduleName))
    const updatedValues = moduleFeatures.reduce((acc, feature) => {
      const metricProps = acc
        .update('logicCount', updateCount('complexQueryCount', feature))
        .update('timerCount', updateCount('timerQueryCount', feature))
        .update('pinCount', updateCount('pinCount', feature))

      return metricProps
    }, initialValues)

    const canInputsCount = Set(
      moduleFeatures
        .flatMap((item) => item.get('canInputs'))
        .toJS()
        .filter((item) => item)
    ).size

    const switchIndicatorCount = Set(
      moduleFeatures
        .flatMap((item) => item.get('switchIndicators'))
        .toJS()
        .filter((item) => item)
    ).size

    return Map({
      ...updatedValues.toJS(),
      canInputsCount,
      switchIndicatorCount,
    })
  }
)

export const makeGetVehicleModuleLogicCount = () =>
  createToJsSelector(vehicleModuleLogicCountSelctor)

const truckTypeSelector = (state, props) =>
  state.getIn(['truck', 'vehicles', props.serialNumber, 'vehicleType'])

export const getTruckType = createSelector(truckTypeSelector, safeToJS)

export const getTruckRevisions = createSelector(
  getTruckState,
  (_, { serialNumber }) => serialNumber,
  (trucks, serialNumber) =>
    safeToJS(trucks.getIn(['vehicles', serialNumber, 'revisions'], []))
)

export const getTruckRevisionsLoading = createSelector(
  getTruckState,
  (_, { serialNumber }) => serialNumber,
  (trucks, serialNumber) =>
    trucks.getIn(['vehicles', serialNumber, 'vehicleRevisionsLoading'])
)

export const getTruckIsPreProduction = createSelector(
  getTruck,
  safeGet('isPreProduction')
)

export const getTruckBuildDate = createSelector(getTruck, safeGet('buildDate'))

export const getPowerTakeOffSummaryData = createSelector(
  vinSelector,
  primaryModuleVersionSelector,
  engineFamilySelector,
  transmissionFamilySelector,
  (vin, primaryModuleVersion, engine, transmission) => ({
    vin,
    primaryModuleVersion,
    engine,
    transmission,
  })
)

export const getEcuParFileData = createSelector(
  moduleListSelector,
  parFileValidationDataSelector,
  (modules, parFileValidation) => {
    const ecuModules = modules.reduce((acc, ecuModule) => {
      const { id, type } = pickToJS(['id', 'type'], ecuModule)
      if (equals(type, ECU)) return acc.push(id.toLowerCase())
      return acc
    }, List())

    return fromJS(
      getParValidationState(
        parFileValidation ? parFileValidation.toJS() : null,
        ecuModules.toJS()
      )
    )
  }
)

export const ecuParFileDataSelector = createSelector(
  getEcuParFileData,
  safeToJS
)

export const getCanUserEditVehicle = createSelector(
  truckTypeSelector,
  getCanEditViewChecPlusVehicle,
  getCanEditViewM2Vehicle,
  (type, canEditPlus = false, canEditM2 = false) => {
    if (!type) return null

    const canEditAndVehicleIsPlus = and(
      equals(canEditPlus, true),
      equals(VEHICLE_TYPE_CHEC_PLUS, type)
    )
    const canEditAndVehicleIsM2 = and(
      equals(canEditM2, true),
      equals(VEHICLE_TYPE_M2, type)
    )

    return cond([
      [always(canEditAndVehicleIsPlus), always(true)],
      [always(canEditAndVehicleIsM2), always(true)],
      [T, always(false)],
    ])()
  }
)

const getItemsByModuleSelector = (
  propName,
  key,
  countPropKey,
  maxCountPropKey
) =>
  createSelector(moduleListSelector, featuresSelector, (modules, features) =>
    features.reduce((prev, curr) => {
      const moduleNameFromCurr = curr.get('moduleName')
      const moduleFromPrev = getInOr(
        List(),
        [moduleNameFromCurr, propName],
        prev
      )
      const ListFromFeature = getInOr(List(), [key], curr)
      const moduleDetails =
        modules.find((item) => item.get('id') === moduleNameFromCurr) || Map()

      const value = Map({
        [propName]: moduleFromPrev.concat(ListFromFeature).toSet(),
        count: moduleDetails.get(countPropKey) || 0,
        maxCount: moduleDetails.get(maxCountPropKey) || 0,
      })

      return prev.set(moduleNameFromCurr, value)
    }, Map())
  )

export const getSwitchesByModuleForVehicle = createSelector(
  getItemsByModuleSelector(
    'switches',
    'switchIndicators',
    'switchIndicatorCount',
    'switchIndicatorMax'
  ),
  safeToJS
)

export const getCanInputsByModuleForVehicle = createSelector(
  getItemsByModuleSelector(
    'canInputs',
    'canInputs',
    'canInputCount',
    'maxCanInputs'
  ),
  safeToJS
)

/**
 * A selector to find out if the dbCodes array contains a 35M code.
 *
 * SerialNumber is required for getVehicleDataBookCodes
 * @param {{ string }} object.serialNumber
 * @return {string || false}
 */
const db35mCodeSelector = createSelector(
  getVehicleDataBookCodes,
  (databookCodes) => databookCodes.find((code = '') => code.includes('35M'))
)

/**
 * A getter for a vehicle 35M code
 *
 * SerialNumber is required for db35mCodeSelector
 * @param {{ string }} object.serialNumber
 * @return {string || false}
 */
export const getDb35mCode = createSelector(db35mCodeSelector, safeToJS)

/**
 * Returns true if any QPM ECU (BUS_MODULES) on the vehicle has an app variant
 * >= MIN_APP_VARIANT_FOR_SWITCH_INDICATOR (0x118), meaning the Switch Indicators
 * Used column / ECU-card metric should be displayed.
 *
 * @param {{ serialNumber: string }} props
 * @returns {boolean}
 */
export const getShowSwitchIndicatorsForVehicle = createSelector(
  moduleListSelector,
  (modules) =>
    modules.some((module) => {
      if (!BUS_MODULES.includes(module.get('id'))) return false
      const ecuVariant = module.get('ecuVariant')
      if (!ecuVariant) return false
      const parts = ecuVariant.split('_')
      const hexPart = parts.length > 1 ? parts[1] : ''
      const num = parseInt(hexPart, 16)
      return !Number.isNaN(num) && num >= MIN_APP_VARIANT_FOR_SWITCH_INDICATOR
    })
)
